/**
 * Puts OPC's "Edit Options" button back on the line it belongs to.
 *
 * WHY THEY END UP MISPLACED (established by experiment 2026-08-31, after four
 * wrong hypotheses — do not replace this with a guess):
 *
 *   - Live carts mixing non-OPC products with configured doors: correct.
 *   - A fresh page load with the fee line already in the cart, this feature's
 *     scripts disabled: ALSO CORRECT. So neither a non-OPC line, nor one
 *     sitting at cart index 0, nor one displaying line-item properties, is
 *     enough to confuse OPC.
 *   - The fee line added at runtime, followed by this feature re-rendering the
 *     cart sections (custom-color-fee.js refresh()): MISPLACED, every button
 *     one row down.
 *
 * So the trigger is ours: OPC injects its buttons, we replace the cart markup
 * underneath it, and OPC re-injects against positional state that no longer
 * matches the DOM. It self-corrects on the customer's next page load, but it
 * is wrong at exactly the moment they are looking at the cart.
 *
 * The button's own attributes stay correct throughout — OPC stamps the real
 * productid, variantid and linekey on it. Only placement drifts. So this does
 * not model OPC's logic at all: it reads the linekey OPC itself wrote and
 * moves the button into the cart item whose data-line-key matches. That holds
 * whatever OPC's placement rule turns out to be, and no-ops when placement is
 * already right — which is every case except the one above.
 *
 * It owns: moving misplaced buttons. It does NOT own the fee itself — see
 * custom-color-model.js and custom-color-reconciler.js.
 */
(function (root, factory) {
  'use strict';
  const isNode = typeof module !== 'undefined' && module.exports;
  const api = factory();
  if (isNode) {
    module.exports = api;
    return;
  }
  root.CustomColorOpcButtons = api;
  api.mount();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const BUTTON_SELECTOR = '.opc-edit-option-click';
  const ITEM_SELECTOR = '.cart-item';

  /**
   * Pure: which buttons are in the wrong container, and where each belongs.
   *
   * Works over plain {lineKey, containerKey} / {key} records so the rule is
   * testable with no DOM. A button whose lineKey names no container in the
   * cart is left alone: that is a line being removed mid-render, and moving it
   * anywhere would be worse than leaving it where it is.
   *
   * @param {Array<{lineKey: string, containerKey: string}>} buttons
   * @param {Array<{key: string}>} containers
   */
  function planRelocations(buttons, containers) {
    const byKey = Object.create(null);
    (containers || []).forEach(function (container) {
      if (container && container.key) byKey[container.key] = container;
    });

    return (buttons || [])
      .filter(function (button) {
        return Boolean(
          button
            && button.lineKey
            && byKey[button.lineKey]
            && button.containerKey !== button.lineKey
        );
      })
      .map(function (button) {
        return { button: button, target: byKey[button.lineKey] };
      });
  }

  /**
   * Where inside the target row the button should go.
   *
   * OPC puts it in the description column, under the line's properties, so
   * mirror that. Falling back to the row itself keeps a misplaced-but-visible
   * button rather than dropping it.
   */
  function dropZone(item) {
    return item.querySelector('.cart-item__description') || item;
  }

  function apply() {
    const items = Array.prototype.slice.call(document.querySelectorAll(ITEM_SELECTOR));
    const containers = items.map(function (item) {
      return { key: item.getAttribute('data-line-key') || '', el: item };
    });

    const buttons = Array.prototype.slice
      .call(document.querySelectorAll(BUTTON_SELECTOR))
      .map(function (el) {
        const item = el.closest(ITEM_SELECTOR);
        return {
          lineKey: el.getAttribute('linekey') || '',
          containerKey: (item && item.getAttribute('data-line-key')) || '',
          el: el,
        };
      });

    planRelocations(buttons, containers).forEach(function (move) {
      dropZone(move.target.el).appendChild(move.button.el);
    });
  }

  /**
   * OPC injects its buttons whenever it finishes rendering, and again after
   * this theme re-renders the cart sections, so a one-shot pass is not enough.
   *
   * Moving a button mutates the tree and re-enters this observer; the second
   * pass finds nothing misplaced and stops, so it settles rather than looping.
   * A microtask coalesces the burst OPC emits while injecting several buttons.
   */
  function mount() {
    if (typeof document === 'undefined') return;

    let queued = false;
    let observer = null;

    function schedule() {
      if (queued) return;
      queued = true;
      Promise.resolve().then(function () {
        queued = false;
        apply();
      });
    }

    // Watch only the cart containers, not the whole document. The buttons only
    // ever drift inside a cart that this feature re-rendered, so observing
    // document.body would wake this on every unrelated DOM change on the page
    // for no benefit. Re-resolved on each cart event because the theme
    // replaces these elements wholesale when it re-renders a section.
    function observe() {
      if (typeof MutationObserver === 'undefined') return;
      const roots = ['cart-form', 'cart-drawer', '.cart-items', '.template-cart']
        .map(function (sel) { return document.querySelector(sel); })
        .filter(Boolean);
      if (!roots.length) return;

      if (observer) observer.disconnect();
      observer = new MutationObserver(schedule);
      roots.forEach(function (root) {
        observer.observe(root, { childList: true, subtree: true });
      });
    }

    function onCartEvent() {
      observe(); // the theme may have just replaced the element we were watching
      schedule();
    }

    document.addEventListener('on:cart:add', onCartEvent);
    document.addEventListener('on:cart:change', onCartEvent);
    document.addEventListener('on:cart-drawer:after-open', onCartEvent);

    observe();
    schedule();
  }

  return { planRelocations: planRelocations, apply: apply, mount: mount };
});
